import classnames from "classnames";
import { bool, func, node, oneOf, string } from "prop-types";

const variantStyles = {
  primary:
    "bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active shadow-sm",
  secondary:
    "bg-secondary text-secondary-foreground border border-secondary-border hover:bg-secondary-hover active:bg-secondary-active shadow-sm",
  destructive:
    "bg-secondary text-destructive border border-destructive hover:bg-destructive-light hover:text-destructive-light-foreground active:bg-destructive-light shadow-sm",
  "destructive-filled":
    "bg-destructive text-destructive-foreground hover:bg-destructive-hover active:bg-destructive-active shadow-sm",
  warning:
    "bg-destructive text-destructive-foreground hover:bg-destructive-hover active:bg-destructive-active shadow-sm",
  ghost:
    "bg-transparent text-secondary-foreground hover:bg-accent active:bg-secondary-active",
  link:
    "bg-transparent text-primary underline-offset-4 hover:underline active:text-primary-active p-0",
};

const sizeStyles = {
  sm: "h-7 px-2.5 text-xs",
  small: "h-7 px-2.5 text-xs",         /* legacy alias */
  md: "h-8 px-3 text-xs",
  medium: "h-8 px-3 text-xs",          /* legacy alias */
  default: "h-8 px-3.5 text-xs",
  normal: "h-8 px-3.5 text-xs",        /* legacy alias */
  lg: "h-9 px-4 text-sm",
  icon: "h-8 w-8 p-0",
};

function Button({
  children,
  className,
  variant = "primary",
  type = "button",
  onClick,
  size = "default",
  isFullWidth,
  isTextCenter,
  loading,
  disabled,
  testId,
  style,
}) {
  const classes = classnames(
    /* base */
    "inline-flex items-center justify-center gap-2 rounded-md font-medium",
    "transition-colors duration-fast",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    /* variant */
    variantStyles[variant] || variantStyles.primary,
    /* size */
    sizeStyles[size] || sizeStyles.default,
    /* modifiers */
    {
      "w-full": isFullWidth,
      "justify-center": isTextCenter,
      "pointer-events-none opacity-50": disabled || loading,
      [className]: className,
    }
  );

  return (
    <button
      disabled={disabled || loading}
      onClick={onClick}
      type={type}
      className={classes}
      data-testid={testId}
      style={style}
    >
      {loading && (
        <span
          className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
          aria-hidden="true"
        />
      )}
      {loading ? <span>Loading…</span> : children}
    </button>
  );
}

Button.propTypes = {
  children: node.isRequired,
  className: string,
  variant: oneOf([
    "primary",
    "secondary",
    "destructive",
    "destructive-filled",
    "warning",
    "ghost",
    "link",
  ]),
  onClick: func,
  size: oneOf(["sm", "small", "md", "medium", "default", "normal", "lg", "icon"]),
  type: oneOf(["button", "submit", "reset"]),
  isFullWidth: bool,
  isTextCenter: bool,
  loading: bool,
  disabled: bool,
  testId: string,
};

export default Button;
